import {
  ReactNode, useEffect, useState, forwardRef, useContext,
} from 'react';
import { useQuery } from '@apollo/client';
import DatePicker from 'react-datepicker';
import { Global, css } from '@emotion/react';
import { styled } from '@mui/material';
import isDate from 'lodash/isDate';
import isArray from 'lodash/isArray';
import { OvColumn } from '../rebalancePage/sharedTypes';
import { FetchSubTradeRequests, FwdOvTextFieldProps, SubTradeSelectedUser } from './subTradesTypes';
import { SubTradeRequest } from '../../../../interfaces/subTradeRequest';
import { FETCH_SUBTRADES_REQUESTS } from '../queriesMutations';
import OvTable from '../OvTable/OvTable';
import {
  Card, CardContent, Button, MenuItem, TextField,
} from '../../../2-component';
import { Grid, Typography } from '../../../1-primative';
import {
  AccountColumn, ClientColumn, SecurityColumn, SettlementDateColumn,
  StatusColumn, TradeColumn, TradeDateColumn, VerificationDateColumn,
} from './subtradesColumns';
import { goalTypes, GoalTypes } from '../../../../interfaces';
import ClientsAutocomplete from '../clientsAutocomplete';
import OvTextField from '../OvTextField';
import { UserContext } from '../../../../providers/userContextProvider';

import 'react-datepicker/dist/react-datepicker.css';

const columns: OvColumn<SubTradeRequest>[] = [
  {
    header: 'Account', accessor: 'account', sort: false, cell: (row): ReactNode => (<AccountColumn data={row} />),
  }, {
    header: 'Client', accessor: 'client', sort: false, cell: (row): ReactNode => (<ClientColumn data={row} />),
  }, {
    header: 'Security', accessor: 'security', sort: false, cell: (row): ReactNode => (<SecurityColumn data={row}/>),
  }, {
    header: 'Trade', accessor: 'trade', sort: false, cell: (row): ReactNode => (<TradeColumn data={row}/>),
  }, {
    header: 'status', accessor: 'expectedCashCents', sort: false, cell: (row): ReactNode => (<StatusColumn data={row}/>),
  }, {
    header: 'Trade Date', accessor: 'tradeDate', sort: false, cell: (row): ReactNode => (<TradeDateColumn data={row}/>),
  }, {
    header: 'Settlement Date', accessor: 'settlementDate', sort: false, cell: (row): ReactNode => (<SettlementDateColumn data={row}/>),
  }, {
    header: 'Verification', accessor: 'verification', sort: false, cell: (row): ReactNode => (<VerificationDateColumn data={row}/>),
  },
];

type SubtradeGroupStates = 'PENDING' | 'COMPLETE' | 'UNSUCCESSFUL' | 'REQUESTED';
type SubtradeStates = 'INITIATED' | 'READY' | 'RECONCILED' | 'CANCELED' | 'REQUESTED';
type SubtradeTypes = 'ANY' | 'BUY' | 'SELL';

const STATES: Record<SubtradeGroupStates, (SubtradeStates | SubtradeStates[])> = {
  PENDING: ['INITIATED', 'READY'],
  COMPLETE: 'RECONCILED',
  UNSUCCESSFUL: 'CANCELED',
  REQUESTED: 'REQUESTED',
};

type SubtradesPageProps = { state: keyof typeof STATES };

const DatePickerWrapper = styled('div')(() => ({
  width: '100%',

  '.react-datepicker-wrapper': {
    width: '100%',
    display: 'block',

    '.react-datepicker__close-icon:after': {
      position: 'absolute',
      padding: '0 6px 0',
      display: 'flex',
      alignItems: 'center',
      bottom: 0,
      right: 0,
      height: 'calc(100% - 20px)',
      fontSize: 24,
      backgroundColor: 'transparent',
      color: 'rgba(0, 0, 0, 0.54)',
    },
  },

  '.react-datepicker__input-container': {
    width: '100%',
    display: 'block',

    '.MuiFormControl-root': {
      width: '100%',
    },
  },
}));

const datePickerGlobalStyles = css`
  .react-datepicker-popper { z-index: 1300; }

  .react-datepicker {
    font-family: Interphases Pro, "Causten Round", Helvetica, sans-serif;
    background-color: #ffffff;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 4px;
    box-shadow: rgba(0, 0, 0, 0.2) 0px 5px 5px -3px,
                rgba(0, 0, 0, 0.14) 0px 8px 10px 1px,
                rgba(0, 0, 0, 0.12) 0px 3px 14px 2px;
    color: rgba(0, 0, 0, 0.87);
    padding: 8px;
    font-size: 0.8125rem;
  }

  .react-datepicker__header {
    background-color: #ffffff;
    border-bottom: none;
    padding-top: 8px;
    padding-bottom: 8px;
  }

  .react-datepicker__navigation {
    top: 10px;
  }
  .react-datepicker__navigation--previous {
    border-right-color: rgba(0, 0, 0, 0.54);
  }
  .react-datepicker__navigation--next {
    border-left-color: rgba(0, 0, 0, 0.54);
  }
   .react-datepicker__navigation:hover *::before {
       border-color: rgba(0, 0, 0, 0.87);
   }
  .react-datepicker__current-month {
    font-size: 0.9375rem;
    font-weight: 500;
    padding-bottom: 8px;
    color: rgba(0, 0, 0, 0.87);
  }

  /* Weekday Names */
  .react-datepicker__day-name {
    font-size: 0.75rem;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.6);
    margin: 0;
    width: 40px;
    line-height: 36px;
  }

  /* Individual Days */
  .react-datepicker__day {
    position: relative;
    width: 40px;
    height: 40px;
    line-height: 36px;
    margin: 2px 0;
    border-radius: 50% !important;
    font-weight: 400;
    color: rgba(0, 0, 0, 0.87);

    &:hover {
      background-color: rgba(0, 0, 0, 0.04);
      border-radius: 50%;
    }

    &:after {
      content: '';
      display: block;
      visibility: hidden;
      position: absolute;
      width: 100%;
      height: 95%;
      top: 50%; left: 50%;
      transform: translate(-50%, -50%);
      border-top: dashed 2px #9e9e9e;
      border-bottom: dashed 2px #9e9e9e;
      border-radius: 0 !important;
      background-color: transparent !important;
    }

    &.react-datepicker__day--outside-month {
      color: rgba(0, 0, 0, 0.38);
    }

    &.react-datepicker__day--today {
      font-weight: 400;
      background-color: transparent;
      border: 1px solid #1976d2;
      color: #1976d2;

      &:hover {
        background-color: rgba(0, 0, 0, 0.04);
      }
    }

    &.react-datepicker__day--selected,
    &.react-datepicker__day--selecting-range-start,
    &.react-datepicker__day--selecting-range-end,
    &.react-datepicker__day--range-start,
    &.react-datepicker__day--range-end {
      background-color: #1976d2;
      color: #ffffff;
      font-weight: 500;
      border-radius: 50%;

      &:hover {
        background-color: #1565c0;
      }

      &.react-datepicker__day--today {
        border: none;
        color: #ffffff
      }
    }

    &.react-datepicker__day--in-range {
      &:not(
        .react-datepicker__day--range-start,
        .react-datepicker__day--range-end
      ) {
        border-radius: 0 !important;
        background-color: rgba(25, 118, 210, 0.12);
      }

      &.react-datepicker__day--range-start {
        &:after {
          visibility: visible;
          width: 50%;
          height: 90%;
          left: 50%;
          transform: translate(0, -50%);
          border-color: transparent;
          background-color: rgba(25, 118, 210, 0.12) !important;
        }
      }

      &.react-datepicker__day--range-end {
        &:after {
          visibility: visible;
          width: 50%;
          height: 90%;
          left: auto; right: 50%;
          transform: translate(0, -50%);
          border-color: transparent;
          background-color: rgba(25, 118, 210, 0.12) !important;
        }
      }
    }

    &.react-datepicker__day--in-selecting-range {
      &:not(
        .react-datepicker__day--selecting-range-start,
        .react-datepicker__day--selecting-range-end
      ) { background-color: transparent !important; }
      &:after { visibility: visible; }
    }

    &.react-datepicker__day--selecting-range-start {
      &:after {
        width: 50%;
        left: auto; right: 0;
        transform: translate(0, -50%);
      }
    }

    &.react-datepicker__day--selecting-range-end {
      &:after {
        width: 50%;
        left: 0;'
        transform: translate(0, -50%);
      }
    }

    .react-datepicker__day--disabled {
      color: rgba(0, 0, 0, 0.38); /* Text disabled */
      cursor: default;
      background-color: transparent;
      &:hover {
        background-color: transparent; /* No hover effect */
      }
    }
  }
`;

const SubtradesPage = ({ state }: SubtradesPageProps) => {
  const { activeOrganization } = useContext(UserContext);

  const [subtrades, setSubtrades] = useState<SubTradeRequest[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(15);
  const [sortColumn, setSortColumn] = useState<keyof SubTradeRequest | null>(null);
  const [sorDirection, setSorDirection] = useState<'asc' | 'desc' | undefined>('asc');
  const [totalItems, setTotalItems] = useState<number>(0);
  const [selected, setSelected] = useState<SubTradeRequest[]>([]);

  const [goal, setGoal] = useState<(keyof typeof GoalTypes) | 'ANY'>('ANY');
  const [trade, setTrade] = useState<SubtradeTypes>('ANY');
  const [client, setClient] = useState<SubTradeSelectedUser | null>(null);

  const [rangeTradeDate, setRangeTrade] = useState<any[]>([null, null]);
  const [startTradeDate, endTradeDate] = rangeTradeDate;

  const [rangeSettlementDate, setRangeSettlementDate] = useState<any[]>([null, null]);
  const [startSettlementDate, endSettlementDate] = rangeSettlementDate;

  const showGoalFilter = false;

  const { loading, refetch } = useQuery(FETCH_SUBTRADES_REQUESTS, {
    fetchPolicy: 'no-cache',
    variables: {
      input: {
        filter: {
          organizationId: activeOrganization?.id,
          states: isArray(STATES[state]) ? STATES[state] : STATES[state],
          userId: client?.id || undefined,
          type: trade !== 'ANY' ? trade : undefined,
          createdAtBefore: rangeTradeDate.filter((date) => isDate(date)).length > 1 ? startTradeDate : undefined,
          createdAtAfter: rangeTradeDate.filter((date) => isDate(date)).length > 1 ? endTradeDate : undefined,
          settlementDateAtBefore: rangeSettlementDate.filter((date) => isDate(date)).length > 1 ? startSettlementDate : undefined,
          settlementDateAtAfter: rangeSettlementDate.filter((date) => isDate(date)).length > 1 ? endSettlementDate : undefined,
        },
        pagination: {
          sortField: sortColumn,
          sortDesc: sorDirection === 'desc',
          perPage: rowsPerPage,
          offSet: (currentPage - 1) * rowsPerPage,
        },
      },
    },
    onCompleted: (response: FetchSubTradeRequests<SubTradeRequest>) => {
      setSubtrades(response.fetchSubTradeRequests.subTradeRequests || []);
      setTotalItems(response.fetchSubTradeRequests.totalCount);
    },
  });

  const listenPage = (page: number, linePerPage: number): void => {
    setCurrentPage(page);
    setRowsPerPage(linePerPage);
  };

  const listenSort = (column: keyof SubTradeRequest | null, direction: 'asc' | 'desc' | undefined): void => {
    setSortColumn(column);
    setSorDirection(direction);
  };

  const listenSelected = (selectedSubtrades: SubTradeRequest[]): void => {
    setSelected(selectedSubtrades);
  };

  const replaceUnderscore = (inputString: string): string => inputString.replace(/_/g, ' ').toLowerCase();

  const capitalize = (str: string): string => {
    if (typeof str !== 'string' || !str.length) { return ''; }
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const normalizeEnum = (str: string): string => capitalize(replaceUnderscore(str.toLowerCase()));

  const FwdOvTextField = forwardRef<HTMLInputElement, FwdOvTextFieldProps>(({
    value, onClick, label, inputProps, ...otherProps
  }, ref) => (
      <OvTextField
        label={label}
        value={value}
        onClick={onClick}
        inputRef={ref}
        variant='outlined'
        {...otherProps}
        InputProps={{
          readOnly: true,
          ...inputProps,
        }}
      />
  ));

  useEffect(() => {
    refetch();
  }, [currentPage, rowsPerPage, sortColumn, sorDirection, client, trade, refetch]);

  return (
    <>
      <Global styles={datePickerGlobalStyles} />

      <Card loading={loading}>
        <CardContent>
          { state !== 'PENDING' && <>
            <Grid container gap={1} justifyContent='space-between' alignItems='center'>
              <Grid item xs={8}>
                <Grid container gap={1} alignItems='center' flexWrap='nowrap'>
                  <Grid item xs={showGoalFilter ? 2 : 3}>
                    <ClientsAutocomplete onChange={(user: SubTradeSelectedUser) => {
                      setClient(user);
                    }} />
                  </Grid>
                  { showGoalFilter && <Grid item xs={2}>
                    <TextField
                      select
                      fullWidth
                      label='Goals'
                      value={goal}
                      onChange={(e: any) => setGoal(e.target.value)}>
                      <MenuItem value='ANY'>Any</MenuItem>
                      { goalTypes.map((value) => <MenuItem value={value}>{normalizeEnum(value)}</MenuItem>) }
                    </TextField>
                  </Grid> }
                  <Grid item xs={showGoalFilter ? 2 : 3}>
                    <TextField
                      select
                      fullWidth
                      label='Trade'
                      value={trade}
                      onChange={(e: any) => setTrade(e.target.value)}>
                      <MenuItem value='ANY'>Any</MenuItem>
                      <MenuItem value='BUY'>Buy</MenuItem>
                      <MenuItem value='SELL'>Sell</MenuItem>
                    </TextField>
                  </Grid>
                  { state !== 'REQUESTED' && <>
                    <Grid item xs={showGoalFilter ? 2 : 3}>
                      <DatePickerWrapper>
                        <DatePicker
                          customInput={<FwdOvTextField label='Create Date' />}
                          selectsRange={true}
                          startDate={startTradeDate}
                          endDate={endTradeDate}
                          onChange={(update: (Date | null)[]) => {
                            setRangeTrade(update);
                          }}
                          isClearable={true}
                        />
                      </DatePickerWrapper>
                    </Grid>
                    <Grid item xs={showGoalFilter ? 2 : 3}>
                      <DatePickerWrapper>
                        <DatePicker
                          customInput={<FwdOvTextField label='Settlement Date' />}
                          selectsRange={true}
                          startDate={startSettlementDate}
                          endDate={endSettlementDate}
                          onChange={(update) => {
                            setRangeSettlementDate(update);
                          }}
                          isClearable={true}
                        />
                      </DatePickerWrapper>
                    </Grid>
                  </> }
                </Grid>
              </Grid>
            </Grid>
          </> }
          { state === 'PENDING' && <>
            <Grid container gap={1} justifyContent='space-between' alignItems='center'>
              <Grid item xs={6}>
                <Typography>Drag column headers here to set row groups</Typography>
              </Grid>
              <Grid item xs='auto'>
                <Grid container gap={1}>
                  <Grid item xs='auto'>
                    <Button
                      label={'Verify'}
                      variant='filled'
                      disabled={!selected.length}
                    />
                  </Grid>
                  <Grid item xs='auto'>
                    <Button
                      label={'Cancel Trades'}
                      variant='filled'
                      disabled={!selected.length}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </> }
        </CardContent>
        <OvTable<SubTradeRequest>
          data={subtrades} columns={columns} loading={loading} checkbox={state === 'PENDING'}
          linesPerPage={rowsPerPage} initialPage={currentPage} totalLines={totalItems}
          onPage={listenPage} onSort={listenSort} onSelect={listenSelected} pagination={true} />
      </Card>
    </>
  );
};

export default SubtradesPage;
